import React, { useState } from 'react';
import { Modal, Input, Form, Typography, Space, Tag, Alert, Button } from 'antd';
import { CheckCircleFilled, TeamOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
  vehicleRequestService,
  VehicleRequestType,
  SubmitVehicleRequestResponse,
} from '../../api/services/vehicleRequestService';

const { Text, Title } = Typography;
const { TextArea } = Input;

interface MissingVehicleModalProps {
  open: boolean;
  onClose: () => void;
  requestType: VehicleRequestType;
  initialName?: string;
  context?: {
    makeId?: string;
    makeName?: string;
  };
}

const TYPE_LABEL: Record<VehicleRequestType, string> = {
  make: 'Make',
  model: 'Model',
};

const TYPE_META: Record<VehicleRequestType, { color: string; bg: string; border: string; example: string }> = {
  make:  { color: '#FE5A1D', bg: '#fff7f4', border: '#ffd0bb', example: 'e.g. BYD, Lynk & Co' },
  model: { color: '#FE5A1D', bg: '#fff7f4', border: '#ffd0bb', example: 'e.g. Atto 3, Han EV' },
};

// Shared button styles — single source of truth
const BTN_PRIMARY: React.CSSProperties = {
  height: 40,
  borderRadius: 10,
  fontWeight: 600,
  fontSize: 14,
  background: '#FE5A1D',
  borderColor: '#FE5A1D',
  boxShadow: '0 2px 8px rgba(254,90,29,0.25)',
};

const BTN_DEFAULT: React.CSSProperties = {
  height: 40,
  borderRadius: 10,
  fontWeight: 500,
  fontSize: 14,
};

export const MissingVehicleModal: React.FC<MissingVehicleModalProps> = ({
  open,
  onClose,
  requestType,
  initialName = '',
  context = {},
}) => {
  const { t } = useTranslation();
  const [name, setName] = useState(initialName);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<SubmitVehicleRequestResponse | null>(null);
  const [error, setError] = useState('');

  const meta = TYPE_META[requestType];

  const handleClose = () => {
    setName(initialName);
    setNotes('');
    setResult(null);
    setError('');
    onClose();
  };

  const handleSubmit = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setError(`Please enter the ${TYPE_LABEL[requestType].toLowerCase()} name.`);
      return;
    }
    setError('');
    setLoading(true);
    try {
      const res = await vehicleRequestService.submit({
        type: requestType,
        name: trimmed,
        notes: notes.trim() || undefined,
        makeId: context.makeId,
        makeName: context.makeName,
      });
      setResult(res);
    } catch {
      setError('Failed to submit request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Context breadcrumb parts
  const contextParts = [context.makeName].filter(Boolean) as string[];

  // ── Custom footer — full control over button appearance ────────────
  const footer = result ? (
    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
      <Button type="primary" onClick={handleClose} style={BTN_PRIMARY}>
        Done
      </Button>
    </div>
  ) : (
    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
      <Button onClick={handleClose} style={BTN_DEFAULT}>
        Cancel
      </Button>
      <Button
        type="primary"
        loading={loading}
        disabled={!name.trim()}
        onClick={handleSubmit}
        style={BTN_PRIMARY}
      >
        Submit Request
      </Button>
    </div>
  );

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      footer={footer}
      width={480}
      destroyOnClose
      styles={{ body: { padding: 0 } }}
      title={null}
      closable={!loading}
    >
      {/* ── Coloured header strip ── */}
      <div
        style={{
          background: `linear-gradient(135deg, ${meta.bg} 0%, #ffffff 100%)`,
          borderBottom: `1px solid ${meta.border}`,
          borderRadius: '8px 8px 0 0',
          padding: '20px 24px 16px',
          display: 'flex',
          alignItems: 'flex-start',
          gap: 14,
        }}
      >
        {/* Icon circle */}
        <div
          style={{
            width: 42,
            height: 42,
            borderRadius: 12,
            background: meta.color,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            boxShadow: `0 4px 12px ${meta.color}40`,
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <path
              d="M12 5v14M5 12h14"
              stroke="#fff"
              strokeWidth="2.5"
              strokeLinecap="round"
            />
          </svg>
        </div>

        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <span style={{ fontSize: 16, fontWeight: 700, color: '#111827' }}>
              Request Missing {TYPE_LABEL[requestType]}
            </span>
            <Tag
              style={{
                background: meta.bg,
                color: meta.color,
                borderColor: meta.border,
                borderRadius: 20,
                fontWeight: 700,
                fontSize: 10,
                lineHeight: '18px',
                padding: '0 8px',
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
              }}
            >
              {TYPE_LABEL[requestType]}
            </Tag>
          </div>
          <Text type="secondary" style={{ fontSize: 12.5 }}>
            Not found in our list? Request it to be added.
          </Text>
        </div>
      </div>

      {/* ── Body ── */}
      <div style={{ padding: '20px 24px 4px' }}>
        {/* ── SUCCESS state ── */}
        {result ? (
          <Space direction="vertical" size={16} style={{ width: '100%', padding: '8px 0 16px' }}>
            <div style={{ textAlign: 'center', padding: '4px 0' }}>
              <CheckCircleFilled
                style={{ fontSize: 52, color: '#10b981', marginBottom: 12, display: 'block' }}
              />
              <Title level={5} style={{ margin: '0 0 6px', color: '#111827' }}>
                {result.alreadyRequested ? 'Vote Added!' : 'Request Submitted!'}
              </Title>
              <Text type="secondary" style={{ fontSize: 13 }}>
                {result.alreadyRequested
                  ? `"${name.trim()}" was already in the queue. Your vote has been counted.`
                  : `"${name.trim()}" has been added to the review queue.`}
              </Text>
            </div>

            <div
              style={{
                background: '#f8fafc',
                border: '1px solid #e5e7eb',
                borderRadius: 12,
                padding: '12px 16px',
                display: 'flex',
                alignItems: 'center',
                gap: 12,
              }}
            >
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 10,
                  background: '#fff3ee',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <TeamOutlined style={{ fontSize: 18, color: '#FE5A1D' }} />
              </div>
              <div>
                <Text strong style={{ fontSize: 13, color: '#111827', display: 'block' }}>
                  {result.requestCount}{' '}
                  {result.requestCount === 1 ? 'user has' : 'users have'} requested this
                </Text>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Most-requested items are approved first.
                </Text>
              </div>
            </div>
          </Space>
        ) : (
          /* ── FORM state ── */
          <Space direction="vertical" size={14} style={{ width: '100%', paddingBottom: 16 }}>
            {/* Context breadcrumb */}
            {contextParts.length > 0 && (
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  background: '#f4f5f7',
                  borderRadius: 8,
                  padding: '6px 12px',
                  fontSize: 12,
                }}
              >
                <Text type="secondary" style={{ fontSize: 12 }}>Under:</Text>
                {contextParts.map((part, i) => (
                  <React.Fragment key={i}>
                    <Text style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>{part}</Text>
                    {i < contextParts.length - 1 && (
                      <Text type="secondary" style={{ fontSize: 11 }}>›</Text>
                    )}
                  </React.Fragment>
                ))}
              </div>
            )}

            <Form layout="vertical" style={{ margin: 0 }} onFinish={handleSubmit}>
              <Form.Item
                label={
                  <Text strong style={{ fontSize: 13 }}>
                    {TYPE_LABEL[requestType]} Name <span style={{ color: '#ef4444' }}>*</span>
                  </Text>
                }
                style={{ marginBottom: 14 }}
              >
                <Input
                  value={name}
                  onChange={e => {
                    setName(e.target.value);
                    if (error) setError('');
                  }}
                  placeholder={meta.example}
                  size="large"
                  style={{ borderRadius: 10 }}
                  onPressEnter={handleSubmit}

                />
              </Form.Item>

              <Form.Item
                label={
                  <span style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>
                    Additional Notes{' '}
                    <Text type="secondary" style={{ fontWeight: 400, fontSize: 12 }}>
                      (optional)
                    </Text>
                  </span>
                }
                style={{ marginBottom: 0 }}
              >
                <TextArea
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  placeholder={t('missing_vehicle.ph_notes')}
                  rows={3}
                  style={{ borderRadius: 10, resize: 'none', padding: '10px 14px' }}
                  maxLength={300}
                  showCount
                />
              </Form.Item>
            </Form>

            {error && (
              <Alert message={error} type="error" showIcon style={{ borderRadius: 9 }} />
            )}

            <div
              style={{
                background: '#f8fafc',
                borderRadius: 9,
                padding: '10px 14px',
                fontSize: 11.5,
                color: '#9ca3af',
                lineHeight: 1.5,
              }}
            >
              Requests are reviewed by our team. The most-requested items are approved
              first — you'll be able to select the approved item the next time you
              create a listing.
            </div>
          </Space>
        )}
      </div>
    </Modal>
  );
};
